import React from "react";
import Overlay from "../Overlay";
import SimpleTable from "../tables/SimpleTable";
import FilterCard, { FIELD_TYPE, ReactSelect } from "../FilterCard";
import {
  getBoolSelectYN,
  getNumPages,
  getPageOptions,
  hideModal,
  showModalNoOutsideClick,
  slicePages,
} from "../../util/FormatUtil";
import { sweetalert } from "../../App";
import Select from "react-select";
import { MetaDataMethodCode } from "../../types/MetaDataMethodCode";
import { MethodCodeManagementModal } from "../modals/MethodCodeManagementModal";
import NetworkUtil from "../../network/NetworkUtil";
import MethodCodeAPI from "../../network/MethodCodeAPI";
import { Column } from "../tables/TableBase";
import { Sorter, TableOrder } from "../../sorting/Sorter";
import PaginationTool from "../PaginationTool";
import { isBlank, isOnlyWhitespace } from "../../util/ValidationUtil";
import SystemAPI from "../../network/SystemAPI";
import { fetchFieldPermissions } from "../../util/FieldPermissionsFetcher";
import {AdminPages} from "./AdminNav";

const ITEMS_PER_PAGE = 25;

interface MethodCodeManagementState {
  showLoading: boolean;
  selectedCode: MetaDataMethodCode;
  methodCodeQueryResults;
  tableData: {}[];
  pageCustomQuestions?;
  filter?: {
    Name: [];
    Description: [];
    Active: [];
  };
  direction?: TableOrder;
  page_options: ReactSelect[];
  selected_page?: { label; value };
  methodCodeList: { label: string; value: string }[];
  descriptionList: { label: string; value: string }[];
  userRoles?: string;
  fieldPermissions?: Record<string, number[]>;
}

export class MethodCodeManagement extends React.Component<
  any,
  MethodCodeManagementState
> {

  constructor(props) {
    super(props);

    this.state = {
      showLoading: false,
      selectedCode: {} as MetaDataMethodCode,
      tableData: [] as any,
      methodCodeQueryResults: [] as any,
      pageCustomQuestions: [],
      filter: { Name: [], Description: [], Active: [] },
      direction: "asc",
      page_options: [{ label: "1", value: "1" }],
      selected_page: { label: "1", value: 1 },
      methodCodeList: [],
      descriptionList: [],
    };

    this.createOrModifyMethodCode = this.createOrModifyMethodCode.bind(this);
  }

  componentDidMount() {
    document.title = 'Method Code Management';
    fetchFieldPermissions((state) => this.setState(state as any));
    this.loadMethodCodeData();
    this.queryMethodCodeData(1);
  }

  loadMethodCodeData() {
    this.setState({ showLoading: true }, async () => {
      SystemAPI.getCustomQuestions(AdminPages.MethodCodes).then(res => {
        this.setState({ pageCustomQuestions: res.questions })
      })
      MethodCodeAPI.getAllMethodCodes()
        .then((response) => {

          let methodcodenames = response.data.map(mc => ({ label: mc.Name, value: mc.Name }))
          let descriptions = response.data
            .filter(mc => mc.Description && !isBlank(mc.Description) && !isOnlyWhitespace(mc.Description))
            .map(mc => ({
              label: mc.Description.length > 35 ?
                mc.Description.substring(0, 35) + '...' :
                mc.Description,
              value: mc.Description
            }));
          this.setState({ methodCodeList: methodcodenames, descriptionList: descriptions, showLoading: false });
        })
        .catch((e) => {
          console.error(e)
          this.setState({ showLoading: false });
          return sweetalert.fire({
            icon: "info",
            title: "",
            text: "No Method Codes found.",
          });
        });
    });
  }

  queryMethodCodeData(page: number) {
    this.setState({ showLoading: true }, async () => {
      let results = await MethodCodeAPI.filterMethodCodes({
        filter: {
          Name: this.state.filter.Name,
          Description: this.state.filter.Description,
          Active: this.state.filter.Active,
        },
      });

      if (results.data.length < 1) {
        this.setState({ showLoading: false, tableData: [], methodCodeQueryResults: null })
        return sweetalert.fire({ title: '', text: 'No Method Codes returned', icon: 'info' })
      }

      if (!results.success) {
        return sweetalert.fire({
          title: "",
          text: "Unable to filter Method Code data at this time",
          icon: "error",
        });
      }

      this.setState({
        tableData: slicePages(results.data, page, ITEMS_PER_PAGE),
        methodCodeQueryResults: results.data,
        page_options: getPageOptions(getNumPages(results.data, ITEMS_PER_PAGE)),
        showLoading: false,
      });
    });
  }

  createOrModifyMethodCode(methodCode: MetaDataMethodCode) {
    this.setState({ showLoading: true }, async () => {
      if (methodCode.ID === null) {
        let response = await MethodCodeAPI.createMethodCode(methodCode);
        if (!response.success) {
          let msg = "Unable to create Method Code at this time.";
          if (response.reason) {
            msg = response.reason;
          }
          return sweetalert.fire({
            icon: "error",
            title: "Attention",
            text: msg,
            allowOutsideClick: false
          }).then((response) => {
            if (response.isConfirmed) {
              window.location.reload()
            }
          });
        }
      } else {
        let response = await MethodCodeAPI.editMethodCode(methodCode);
        if (!response.success) {
          let msg = "Unable to edit Method Code at this time.";
          if (response.reason) {
            msg = response.reason;
          }
          return sweetalert.fire({
            icon: "error",
            title: "Attention",
            text: msg,
            allowOutsideClick: false
          }).then((response) => {
            if (response.isConfirmed) {
              window.location.reload()
            }
          });
        }
      }

      sweetalert
        .fire({ icon: "success", title: "", text: "Method Code saved" })
        .then(() => {
          this.setState(
            {
              selectedCode: {
                ID: null,
                Name: null,
                Description: null,
              } as MetaDataMethodCode,
            },
            () => {
              this.loadMethodCodeData();
              this.queryMethodCodeData(this.state.selected_page.value);
            }
          );
        });
    });
  }

  handleExportToCSV() {
    this.setState({ showLoading: true }, async () => {
      await NetworkUtil.downloadCSV(
        "/api/admin/methodcodes/csv",
        "Method Code Management Data.xlsx",
        { filter: this.state.filter });
      this.setState({ showLoading: false });
    });
  }

  changePage(page: number) {
    let allServices = this.state.methodCodeQueryResults[0];
    let returnData = slicePages(allServices, page, ITEMS_PER_PAGE);
    this.setState({ tableData: returnData });
  }

  renderMethodCodeFilterFields() {
    return (
      <FilterCard
        fields={[
          {
            label: "Method Code",
            key: "Name",
            type: FIELD_TYPE.SELECT,
            options: this.state.methodCodeList,
            isMapped: true,
            isMulti: true,
            textType: "text",
            isFilter: true
          },
          {
            label: "Description",
            key: "Description",
            type: FIELD_TYPE.SELECT,
            options: this.state.descriptionList,
            isMapped: true,
            isMulti: true,
            textType: "text",
            isFilter: true
          },
          {
            label: "Active?",
            key: "Active",
            type: FIELD_TYPE.SELECT,
            options: [
              { value: 1, label: "Active" },
              { value: 0, label: "Inactive" }
            ],
            isMapped: true,
            isMulti: true,
            textType: "text",
            isFilter: true
          },
        ]}
        filterChanged={(e) => this.setState({ filter: e })}
      />
    );
  }

  useSorter(col: Column) {
    let sorter = new Sorter<any>();

    this.setState({
      tableData: sorter.sortByKey(
        this.state.tableData,
        col.key as keyof any,
        this.state.direction
      ),
      direction: this.state.direction === "asc" ? "desc" : "asc",
    });
  }

  render() {

    return (
      <React.Fragment>
        <PaginationTool />
        <Overlay show_loading={this.state.showLoading} />
        <MethodCodeManagementModal
          selectedMethodCode={this.state.selectedCode}
          pageCustomQuestions={this.state.pageCustomQuestions}
          onSubmit={(result) => {
            hideModal(MethodCodeManagementModal.ID);
            this.createOrModifyMethodCode(result);
          }}
          userRoles={this.state.userRoles}
          fieldPermissions={this.state.fieldPermissions}
        />

        <div className="container-fluid ">
          <div className={"row"}>
            <div className="col-12 col-md-12 col-lg-8 col-xl-5 pt-2">
              <main id="main-content" tabIndex={-1} aria-label="Result Management">
                <div className="card mb-2">
                  <div className="card-header verlag-bold">
                    <h3>Method Code Management</h3>
                  </div>
                  <div className="card-body">
                    {this.renderMethodCodeFilterFields()}
                  </div>
                  <div className="card-footer">
                    <button
                      className={"btn immySubmitButtonOutline"}
                      onClick={() => {
                        this.setState(
                          {
                            selected_page: { label: 1, value: 1 },
                          },
                          () => {
                            this.queryMethodCodeData(
                              this.state.selected_page.value
                            );
                          }
                        );
                      }}
                    >
                      Search
                    </button>
                    <button
                      className="btn btn-outline-success float-right"
                      onClick={() =>
                        this.setState(
                          {
                            selectedCode: {
                              ID: null,
                              Name: "",
                              Description: "",
                              Active: null,
                              CreatedBy: "",
                              CreatedDate: null,
                              UpdatedBy: "",
                              UpdatedDate: null,
                            } as MetaDataMethodCode,
                          },
                          () => {
                            showModalNoOutsideClick(MethodCodeManagementModal.ID);
                          }
                        )
                      }
                    >
                      Create New
                    </button>
                  </div>
                </div>
              </main>
            </div>
            {this.state.tableData && this.state.tableData.length > 0 && (
              <div className="col-12 col-md-12 col-lg-12 pt-2">
                <div className="card mt-2" style={{ marginBottom: '4rem' }}>
                  <div className="card-header verlag-bold stickToTop">
                    <h4 className="text-center text-md-left">
                      Results
                      <section className="tableHeaderSection float-md-right d-flex justify-content-around">
                        <h4 className={"float-md-right"} aria-label="Total Records" role="alert">
                          Total: {(this.state.methodCodeQueryResults[0]).length}
                        </h4>
                        <h4 className="d-inline-block float-right align-middle pr-2 ml-5">
                          Page{" "}
                        </h4>
                        <div className=" align-middle float-right pages ">
                          <Select
                            isSearchable={true}
                            placeholder={"1"}
                            noOptionsMessage={() => "No option"}
                            value={this.state.selected_page}
                            aria-label="Table Page Number"
                            onChange={(e: ReactSelect) =>
                              this.setState({ selected_page: e }, () =>
                                this.changePage(e.value)
                              )
                            }
                            className={"state_select"}
                            options={this.state.page_options}
                          />
                        </div>
                      </section>
                      <button
                        className={"d-none d-md-inline btn btn-outline-primary ml-3"}
                        onClick={() => this.handleExportToCSV()}
                      >
                        Export to CSV
                      </button>
                    </h4>
                  </div>
                  <div className="card-body p-0 m-0 table-responsive">
                    <SimpleTable
                      columns={[
                        {
                          label: "Method Code",
                          key: "Name",
                          popoverText: "Click to Sort By Method Code",
                          rawFormat: (val) => {
                            return (
                              <a
                                href={"#"}
                                className={'tableNameLinkColor'}
                                onClick={() => {
                                  this.setState({ selectedCode: val }, () =>
                                    showModalNoOutsideClick(
                                      MethodCodeManagementModal.ID
                                    )
                                  );
                                }}
                              >
                                {val.Name}
                              </a>
                            );
                          },
                        },
                        {
                          label: "Description",
                          key: "Description",
                          popoverText: "Click to Sort By Description",
                          rawFormat: (val) => {
                            return <div className="descriptionsString">{val.Description}</div>;
                          }
                        },
                        {
                          label: "Active", key: "Active", rawFormat: (val) => {
                            return (
                              <div>{getBoolSelectYN(val.Active)}</div>
                            );
                          }
                        },
                      ]}
                      table_data={this.state.tableData}
                      columnClickedCallback={(col) => {
                        this.useSorter(col);
                      }}
                    />
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </React.Fragment>
    );
  }
}